import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { Autocomplete, Button, Checkbox, FormControlLabel, IconButton, TextField, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate, useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import {
    CreateSearchTaskEvent,
    CreateSearchTaskResult,
    GetSearchTaskEvent,
    GetSearchTaskResult,
    DeleteSearchTaskEvent,
    DeleteSearchTaskEventResult,
    UpdateSearchTaskEvent,
    UpdateSearchTaskEventResult
} from 'bloggers-backend/lib/youtube/domain/events';
import { Channel, SearchTask } from 'bloggers-backend/lib/youtube/domain/SearchTask';
import { GridColDef } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { CreateBloggerEvent, CreateBloggerResult } from 'bloggers-backend/lib/bloggers/domain';
import { DeleteDiaglog } from '../components/DeleteDialog';
import { countries } from 'bloggers-backend/lib/bloggers/dicts/countries';
import { AppContext } from '../AppContext';
import { useUserTime } from '../hooks/useUserTime';
import { ContactsCell } from '../components/searchTask/ContactsCell';
import { ChannelFiltersToolbar } from '../components/searchTask/ChannelFiltersToolbar';
import { channelMatchesFilters, ChannelFilterValues, EMPTY_CHANNEL_FILTERS, getContactsScore } from '../components/searchTask/channelFilters';
import { formatCompactNumber, formatLastVideoRelative, getLastVideoSortValue } from '../components/searchTask/channelFormat';
import { usePollingSearchTask } from '../components/searchTask/usePollingSearchTask';


function getChannelUrl(id: string) {
    return `https://youtube.com/channel/${id}`;
}

export default function SearchTaskPage() {
    const {notificationsService, client} = React.useContext(AppContext);
    const formatUserTime = useUserTime();

  const { id } = useParams();
  const navigate = useNavigate();
  const [error] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [channelFilters, setChannelFilters] = React.useState<ChannelFilterValues>(EMPTY_CHANNEL_FILTERS);
  const now = React.useMemo(() => new Date(), []);

  // Pass the useFormik() hook initial form values and a submit function that will
  // be called when the form is submitted

  const initialValues: SearchTask = {
    id: '',
    query: '',
    channels: [] as Channel[],
    error: '',
    status: 'new',
    createdAt: '',
    maxResults: 50,
    manager: {
        id: "", 
        name: "",
    },
    completed: false,
    countries: [],
    excludedCountries: [],
    strategy: "video",
    order: 'relevance',
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
        setLoading(true);
        if (id) {
            const result = await client<UpdateSearchTaskEvent, UpdateSearchTaskEventResult>({
                event: 'updateSearchTask',
                data: values
            })

            if (result.success) {
                notificationsService.notify({
                    severity: 'success',
                    message: 'SearchTask updated'
                });
            } else {
                notificationsService.notify({
                    severity: 'error',
                    message: `Error: ${JSON.stringify(result.errors)}`
                });
            }
        } else {
            const result = await client<CreateSearchTaskEvent, CreateSearchTaskResult>({
                event: 'createSearchTask',
                data: values
            })

            if (result.success) {
                notificationsService.notify({
                    severity: 'success',
                    message: 'SearchTask created'
                });
                navigate(`/search-tasks/${result.value}`);
            } else {
                notificationsService.notify({
                    severity: 'error',
                    message: `Error: ${JSON.stringify(result.errors)}`
                });
            }
        }
        setLoading(false);
    },
  });
    useEffect(() => {
        if (!id) {
            return;
        }

        (async () => {
            setLoading(true)
            const result = await client<GetSearchTaskEvent, GetSearchTaskResult>(
                {event: 'getSearchTask', data: {id}}
            )

            if (result.success) {
                formik.resetForm({values: result.value})
            } else {
                notificationsService.notify({
                    message: JSON.stringify(result.errors),
                    severity: "error",
                })
            }
            setLoading(false)
        })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    usePollingSearchTask({
        id,
        status: formik.values.status,
        currentValues: formik.values,
        onSearchTaskUpdated: (task) => formik.resetForm({values: task}),
    });

    const visibleChannels = React.useMemo(
        () => formik.values.channels.filter((channel) => channelMatchesFilters(channel, channelFilters, now)),
        [formik.values.channels, channelFilters, now]
    );

    if (error) {
        return (<Box>{error}</Box>);
    }

    const header = id ? 'Edit a search task' : 'Create a new search task'

    const buttonOrLoading = 
        loading
        ? <CircularProgress />
        : (<Button
            onClick={formik.submitForm}
            variant="contained"
            type="submit">Submit
        </Button>)

    async function deleteSearchTask() {
        setDeleteDialogOpen(false);
        setLoading(true);
    
        if (!id) {
          throw new Error('Can not delete blogger with no id');
        }
        const result = await client<DeleteSearchTaskEvent, DeleteSearchTaskEventResult>({
          event: 'deleteSearchTask',
          data: {id}
        });

        if (result.success) {
          notificationsService.notify({
              severity: 'success',
              message: 'Search deleted'
          });
          navigate(`/search-tasks/`);
        } else {
          setLoading(false);
          notificationsService.notify({
              severity: 'error',
              message: JSON.stringify(result.errors)
          });
        }
    }

    async function createBlogger(channel: Channel) {

        channel.isBloggerCreated = true;
        await formik.handleSubmit();
        
        const result = await client<CreateBloggerEvent, CreateBloggerResult>({
            event: 'Blogger.create',
            data: {
                name: channel.name,
                youtube: getChannelUrl(channel.id),
                youtubeChannelId: channel.id,
                country: channel.country,
                instagram: channel.instagram ?? null,
                tikTok: channel.tikTok ?? null,
                facebook: channel.facebook ?? null,
                website: null,
                email: null,
                addEmail: null,
                status: 'candidate'
            }
        });
    
        if (result.success) {
        notificationsService.notify({
            severity: 'success',
            message: 'Blogger created'
        });
        } else {
        if (result.reason === 'validation') {
            notificationsService.notify({
                severity: 'error',
                message: JSON.stringify(result.errors)
            });
        } else {
            notificationsService.notify({
            severity: 'error',
            message: result.reason
            })
        }
        }
    }
    
  const strategies = ['video', 'channel'];
  const orders =['date', 'rating', 'viewCount', 'relevance', 'title', 'videoCount'];

  const columns: GridColDef<Channel>[] = [
    {
        field: 'name',
        headerName: 'name',
        flex: 1,
        minWidth: 160,
        renderCell: ({row}) => (
            <a
                href={getChannelUrl(row?.id)}
                style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'}}
            >
                {row?.name}
            </a>
        )
    },
    {
        field: 'controls',
        headerName: 'controls',
        width: 140,
        renderCell: ({row}) => (
            <IconButton
                aria-label="Add blogger"
                onClick={() => {
                    createBlogger(row)
                }}
                color="primary"
                disabled={row?.isBloggerCreated}
            >
                <AddIcon />
            </IconButton>
        )
    },
    {
        field: 'contacts',
        headerName: 'has contacts',
        width: 170,
        valueGetter: (_value, row) => getContactsScore(row),
        sortComparator: (v1: number, v2: number) => v1 - v2,
        renderCell: ({row}) => <ContactsCell channel={row} />
    },
    {
        field: 'videoDate',
        headerName: 'last video',
        width: 110,
        sortComparator: (v1: string | null | undefined, v2: string | null | undefined) => (
            getLastVideoSortValue(v1) - getLastVideoSortValue(v2)
        ),
        renderCell: ({row}) => (
            <Tooltip title={row?.videoDate ? formatUserTime(row.videoDate) : ''}>
                <span>{formatLastVideoRelative(row?.videoDate, now)}</span>
            </Tooltip>
        )
    },
    {
        field: 'viewCount',
        headerName: 'views',
        width: 100,
        type: 'number',
        renderCell: ({row}) => (
            row.viewCount == null
                ? 'hidden'
                : (
                    <Tooltip title={row.viewCount.toLocaleString()}>
                        <span>{formatCompactNumber(row.viewCount)}</span>
                    </Tooltip>
                )
        )
    },
    {
        field: 'subscriberCount',
        headerName: 'subscribers',
        width: 100,
        type: 'number',
        renderCell: ({row}) => (
            row.subscriberCount == null
                ? 'hidden'
                : (
                    <Tooltip title={row.subscriberCount.toLocaleString()}>
                        <span>{formatCompactNumber(row.subscriberCount)}</span>
                    </Tooltip>
                )
        )
    },
  ];

  return (
    <Box sx={{
        height: '100%',
        width: '100%',
        display: "flex",
        flexDirection: "column"
        }}>
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: '100%',
            m: 1
        }}>
            <Box sx={{ width: '25%' }}>
                <h2 style={{margin: 0}}>
                    {header}{' '}
                    {formik.values.status === 'in progress'
                        ? <CircularProgress size={16} sx={{verticalAlign: 'middle'}} />
                        : `(${formik.values.status})`}
                </h2>
            </Box>
            <Box sx={{ width: '75%' }}>
                <ChannelFiltersToolbar
                    value={channelFilters}
                    onChange={setChannelFilters}
                />
            </Box>
        </Box>
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            flex: 1,
            minHeight: 0
        }}>
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            '& > :not(style)': { m: 1 },
            width: '25%',
            height: '60%'
        }}>
            <TextField
                id="query"
                name="query"
                label="query"
                variant="outlined"
                required
                disabled={id !== undefined}
                onChange={formik.handleChange}
                value={formik.values.query}
            />
            <Autocomplete
                id="strategy"
                disabled={id !== undefined}
                onChange={(_, value) => formik.setFieldValue('strategy', value)}
                options={strategies}
                value={formik.values.strategy}
                renderInput={(params) =>
                <TextField {...params}
                    error={!!formik.errors.strategy}
                    helperText={formik.errors.strategy || ""}
                    id="strategy"
                    label="strategy"
                />}
            />
            <Autocomplete
                id="order"
                disabled={id !== undefined}
                onChange={(_, value) => formik.setFieldValue('order', value)}
                options={orders}
                value={formik.values.order}
                renderInput={(params) =>
                <TextField {...params}
                    error={!!formik.errors.order}
                    helperText={formik.errors.order || ""}
                    id="order"
                    label="order"
                />}
            />
            <TextField
                id="maxResults"
                name="maxResults"
                label="max results"
                variant="outlined"
                required
                disabled={id !== undefined}
                onChange={formik.handleChange}
                value={formik.values.maxResults}
            />
            <Autocomplete
                multiple
                id="countries"
                options={countries}
                disabled={id !== undefined}
                value={formik.values.countries}
                onChange={(_, value) => formik.setFieldValue('countries', value)}
                renderInput={(params) => (
                <TextField variant="outlined" {...params} label="countries" placeholder="countries" />
                )}
            />
            <Autocomplete
                multiple
                id="excludedCountries"
                options={countries}
                disabled={id !== undefined}
                value={(formik.values.excludedCountries) || []}
                onChange={(_, value) => formik.setFieldValue('excludedCountries', value)}
                renderInput={(params) => (
                <TextField variant="outlined" {...params} label="excluded countries" placeholder="excluded countries" />
                )}
            />
            {
                id !== undefined
                ? (
                    <>
                        <TextField
                            id="createdBy"
                            name="createdAt"
                            label="created at"
                            variant="outlined"
                            required
                            disabled={true}
                            value={formatUserTime(formik.values.createdAt)}
                        />
                        <TextField
                            id="manager"
                            name="manager"
                            label="manager"
                            variant="outlined"
                            disabled={true}
                            value={formik.values.manager?.name ?? ""}
                        />
                        <FormControlLabel
                            label="Completed"
                            control={
                            <Checkbox
                                checked={formik.values.completed}
                                value={formik.values.completed}
                                onChange={(event) => {
                                    formik.setFieldValue('completed', event.target.checked)
                                }}
                            />
                        }
                        />
                    </>
                )
                : <></>
            }
            {formik.values.error && (
                <pre>
                    {formik.values.error}
                </pre>
            )}
            {buttonOrLoading}

            <DeleteDiaglog
                isOpen={deleteDialogOpen}
                deleteWhat={"search task"}
                userRejects={() => setDeleteDialogOpen(false)}
                userAccepts={deleteSearchTask}
            />
            <Button
                variant="contained"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                >Delete
            </Button>
        </Box>
        <Box sx={{ width: '75%', m: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ flex: 1 }}>
                <DataGridPro
                    rows={visibleChannels}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'videoDate', sort: 'desc' }],
                        },
                    }}
                    columns={columns}
                    pagination
                    pageSizeOptions={[10, 100, 1000]}
                    disableRowSelectionOnClick
                />
            </Box>
        </Box>
        </Box>
    </Box>
  )
};